// Header component for the application top bar
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { IconButton, Headline, Select } from '@yaradigitallabs/ahua-react';
import logo from '@/assets/logo.svg';
import { UserProfile } from '@/components/UserProfile';
import { PlantsOptions } from '@/utils/enums';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { selectSessionUser } from '@/features/session/store/session.selector';
import { selectConfig } from '@/app/selector';
import { addConfig } from '@/app/coreSlice';
import appLocalStorage from '@/features/session/store/appLocalStorage';
import { getClassNames } from '@/utils/helpers';
import { useLocation, useNavigate } from 'react-router-dom';
import routes from '@/app/routes';

// Props for Header component
// title: optional page title
// signOut: callback for user sign out
type HeaderProps = {
  title?: string;
  signOut: () => void;
};

const PlantSelectionClassNames = getClassNames(
  '[&>div>button]:bg-transparent! [&>div>button]:border-0! ', // Button styles
  '[&>div>label]:text-white!', // Label styles
  '[&>div>button>svg]:hidden! [&>div>button>svg]:stroke-white! [&>div>button>span>div>svg]:text-white! [&>div>button>span>div>svg]:stroke-white!' // Icon styles
);

// Header renders the top navigation bar with logo, title, and action icons
export const Header: React.FC<HeaderProps> = ({ title, signOut }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const userProfile = useAppSelector(selectSessionUser);
  const globalConfig = useAppSelector(selectConfig);
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  // Handler for notification/info icon click
  const handleNotificationClick = () => {
    console.log('Notification clicked');
  };

  // Handler for profile icon click (toggles dropdown)
  const handleProfileClick = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handlePlantChange = (value: string) => {
    dispatch(addConfig({ ...globalConfig, productionUnit: value }));
    appLocalStorage.setProductionUnit(value);
    if (
      location.pathname === routes.MATCH_MAKER ||
      location.pathname === routes.PRODUCTION_REPORTING
    ) {
      window.location.reload();
    } else if (location.pathname.startsWith(routes.MATCH_MAKER)) {
      navigate(routes.MATCH_MAKER, { replace: true });
    } else if (location.pathname.startsWith(routes.PRODUCTION_REPORTING)) {
      navigate(routes.PRODUCTION_REPORTING, { replace: true });
    }
  };

  // Effect to close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const plantSelectionItems = useMemo(() => {
    return PlantsOptions.filter(op => userProfile?.plants?.includes(op.value));
  }, [userProfile]);

  return (
    // Fixed header bar with logo, title, and action icons
    <div className="bg-[#102D6A] fixed top-0 left-0 w-full z-50 shadow-md">
      <div className="flex items-center pl-2 pr-4 w-full gap-4">
        {/* Left Section - Yara Logo */}
        <div className="flex items-center p-4">
          <img src={logo} alt="Yara Logo" className="h-6 w-6" />
        </div>

        <div className="flex gap-4 justify-between items-center w-full">
          {/* Center Section - Title Bar Container */}
          <Headline
            size="2xs"
            css={{ color: 'white', letterSpacing: '0.01em' }}
          >
            {title}
          </Headline>

          {/* Right Section - Action Icons */}
          <div className="flex items-center gap-2">
            <div className="w-[200px]">
              <Select
                label="Plant"
                items={plantSelectionItems}
                disabled={plantSelectionItems.length <= 1}
                size="xs"
                onChange={handlePlantChange}
                value={
                  appLocalStorage.getProductionUnit() ||
                  globalConfig.productionUnit ||
                  ''
                }
                className={PlantSelectionClassNames}
              />
            </div>
            {/* Information Icon */}
            <IconButton
              variant="ghost"
              size="xs"
              onClick={handleNotificationClick}
              className="p-3 bg-none text-white"
              title="Information"
              colorConcept="inverse"
              icon="Info"
            />

            {/* Profile Icon with Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <IconButton
                variant="ghost"
                size="xs"
                onClick={handleProfileClick}
                className="p-3 bg-none text-white"
                title="User Profile"
                colorConcept="inverse"
                icon="Profile"
              />

              {/* Dropdown Menu for user profile actions */}
              {isDropdownOpen && <UserProfile signOut={signOut} />}
            </div>

            {/* Application Icon */}
            <IconButton
              variant="ghost"
              size="xs"
              onClick={handleNotificationClick}
              className="p-3 bg-none text-white"
              title="Applications"
              colorConcept="inverse"
              icon="Bento"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Header;
